import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sessionsAPI, CreateSessionInput } from '../api/client';
import { AdminLayout } from '../components/Layout';
import './CreateSession.css';

/**
 * Create Session Page
 *
 * Form for creating a new proof session with customer details.
 */
const CreateSession = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<CreateSessionInput>({
    customer_name: '',
    customer_email: '',
    session_notes: '',
    send_email_invite: false,
    notify_admin: true,
    expiration_enabled: true,
    expiration_days: 7,
    max_revisions: 1,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean | number = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      newValue = parseInt(value, 10) || 0;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error on input change
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.customer_name.trim()) {
      setError('Customer name is required');
      return false;
    }

    if (!formData.customer_email.trim()) {
      setError('Customer email is required');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customer_email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await sessionsAPI.create({
        customer_name: formData.customer_name.trim(),
        customer_email: formData.customer_email.trim(),
        session_notes: formData.session_notes?.trim() || undefined,
        send_email_invite: formData.send_email_invite,
        notify_admin: formData.notify_admin,
        max_revisions: formData.max_revisions,
      });

      // Navigate to the new session's detail page
      navigate(`/admin/sessions/${response.session.id}`, {
        replace: true,
        state: { newSession: true, proofLink: response.proof_link },
      });
    } catch (err) {
      console.error('Failed to create session:', err);

      if (typeof err === 'object' && err !== null && 'response' in err) {
        const response = (err as { response?: { data?: { error?: string } } }).response;
        setError(response?.data?.error || 'Failed to create session. Please try again.');
      } else {
        setError('Failed to create session. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="create-session">
        {/* Breadcrumb */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/admin/dashboard">Dashboard</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">New Session</span>
        </nav>

        {/* Page Header */}
        <div className="create-session-header">
          <h1>Create New Session</h1>
          <p className="create-session-subtitle">
            Set up a new proof review session for your customer.
          </p>
        </div>

        {/* Form Card */}
        <div className="form-card">
          {/* Error Message */}
          {error && (
            <div className="form-error" role="alert">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Customer Name */}
            <div className="form-group">
              <label htmlFor="customer_name">
                Customer Name <span className="required">*</span>
              </label>
              <input
                id="customer_name"
                name="customer_name"
                type="text"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="Enter customer name"
                disabled={isSubmitting}
                required
                autoFocus
              />
            </div>

            {/* Customer Email */}
            <div className="form-group">
              <label htmlFor="customer_email">
                Customer Email <span className="required">*</span>
              </label>
              <input
                id="customer_email"
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleChange}
                placeholder="customer@example.com"
                disabled={isSubmitting}
                required
              />
              <p className="form-help">
                The customer will receive proof review links at this address.
              </p>
            </div>

            {/* Session Notes */}
            <div className="form-group">
              <label htmlFor="session_notes">
                Notes <span className="optional">(optional)</span>
              </label>
              <textarea
                id="session_notes"
                name="session_notes"
                value={formData.session_notes}
                onChange={handleChange}
                placeholder="Add any internal notes about this session..."
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Send Email Invite */}
            <div className="form-group form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="send_email_invite"
                  checked={formData.send_email_invite}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <span className="checkbox-label">
                  Send email invite to customer
                </span>
              </label>
              <p className="form-help">
                If enabled, an email with the proof link will be sent after you upload photos.
              </p>
            </div>

            {/* Notify Admin on Completion */}
            <div className="form-group form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="notify_admin"
                  checked={formData.notify_admin}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <span className="checkbox-label">
                  Notify me when customer completes review
                </span>
              </label>
              <p className="form-help">
                You'll receive an email when the customer finishes reviewing all photos.
              </p>
            </div>

            {/* Session Expiration */}
            <div className="form-section">
              <h3 className="form-section-title">Session Expiration</h3>

              <div className="form-group form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="expiration_enabled"
                    checked={formData.expiration_enabled}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <span className="checkbox-label">
                    Enable session expiration
                  </span>
                </label>
                <p className="form-help">
                  When enabled, the proof link will expire after the specified number of days.
                </p>
              </div>

              {formData.expiration_enabled && (
                <div className="form-group expiration-days-group">
                  <label htmlFor="expiration_days">
                    Expires after
                  </label>
                  <div className="expiration-input-wrapper">
                    <input
                      id="expiration_days"
                      name="expiration_days"
                      type="number"
                      min="1"
                      max="90"
                      value={formData.expiration_days}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="expiration-days-input"
                    />
                    <span className="expiration-days-label">days</span>
                  </div>
                  <p className="form-help expiration-preview">
                    Link expires: {new Date(Date.now() + (formData.expiration_days || 7) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Revision Limits */}
            <div className="form-section">
              <h3 className="form-section-title">Revision Limits</h3>

              <div className="form-group revision-limit-group">
                <label htmlFor="max_revisions">
                  Maximum Revisions Per Photo
                </label>
                <div className="revision-input-wrapper">
                  <input
                    id="max_revisions"
                    name="max_revisions"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.max_revisions}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="revision-limit-input"
                  />
                  <span className="revision-limit-label">revision{formData.max_revisions !== 1 ? 's' : ''}</span>
                </div>
                <p className="form-help">
                  How many times a customer can request changes per photo. After reaching the limit, they can only approve.
                </p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <Link to="/admin/dashboard" className="btn-cancel">
                Cancel
              </Link>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="btn-spinner" />
                    Creating...
                  </>
                ) : (
                  'Create Session'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateSession;
